import { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'

import { LOCAL_STORAGE_KEYS } from '@/lib/constants'
import { InvalidGrantError } from '@/lib/errors/InvalidGrantError'
import { ResourceExpiredError } from '@/lib/errors/ResourceExpiredError'
import {
  isConstraintViolationListError,
  isError,
} from '@/lib/helpers/helperFunctions'
import authService from '@/lib/services/authService'
import { ConstraintViolation, ConstraintViolationListError } from '@/types/api'

class ErrorTypeService {
  isApiError(error: unknown): error is AxiosError<{
    'hydra:title': string
    'hydra:description': string
  }> {
    if (!this.isAxiosError(error)) {
      return false
    }

    const responseData = error.response?.data

    return !!(
      responseData?.hasOwnProperty('hydra:title') &&
      responseData?.hasOwnProperty('hydra:description')
    )
  }

  isAxiosHttpError(error: unknown, httpStatusCode: number) {
    return (
      this.isAxiosError(error) &&
      (error.response?.status === httpStatusCode ||
        error.status === httpStatusCode)
    )
  }

  isForbiddenError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.FORBIDDEN)
  }

  isUnauthorizedError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.UNAUTHORIZED)
  }

  isBadRequestError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.BAD_REQUEST)
  }

  isUnprocessibleEntityError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.UNPROCESSABLE_ENTITY)
  }

  is404Error(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.NOT_FOUND)
  }

  isInternalServerError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.INTERNAL_SERVER_ERROR)
  }

  isConflictError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.CONFLICT)
  }

  /**
   * The referral was created, but its meeting couldn't be booked (e.g. the
   * calendar connection failed), so it fell back to having no meeting.
   */
  isMeetingNotBookedError(error: unknown): error is AxiosError<{
    'hydra:title': string
    'hydra:description': string
  }> {
    return (
      this.isApiError(error) &&
      this.isAxiosHttpError(error, StatusCodes.FAILED_DEPENDENCY)
    )
  }

  isTooManyRequestsError(error: unknown): error is AxiosError {
    return this.isAxiosHttpError(error, StatusCodes.TOO_MANY_REQUESTS)
  }

  isExpiredResourceError(error: unknown): error is ResourceExpiredError {
    return error instanceof ResourceExpiredError
  }

  isInvalidGrantError(error: unknown): error is InvalidGrantError {
    return error instanceof InvalidGrantError
  }

  isAxiosError(error: unknown): error is AxiosError {
    return error instanceof AxiosError
  }

  isNetworkError(error: unknown): error is AxiosError {
    return this.isAxiosError(error) && error.code === 'ERR_NETWORK'
  }

  isAbortedRequestError(error: unknown): error is AxiosError {
    return (
      this.isAxiosError(error) &&
      error.code === AxiosError.ECONNABORTED &&
      error.message === 'Request aborted'
    )
  }

  isTokenError(error: unknown): boolean {
    if (!this.isApiError(error)) {
      return false
    }

    const statusCode = error?.response?.status
    const errorDescription = error.response?.data?.['hydra:description']

    return (
      statusCode === StatusCodes.UNAUTHORIZED &&
      (errorDescription === 'Invalid token' ||
        errorDescription === 'Expired token')
    )
  }

  /**
   * Registration (Keycloak first login) blocked because the email is another
   * user's verified public facing alias.
   */
  isPublicEmailAliasRegistrationError(error: unknown): boolean {
    if (!this.isApiError(error)) {
      return false
    }

    return (
      error.response?.status === StatusCodes.UNAUTHORIZED &&
      error.response?.data?.['hydra:description']?.includes(
        'public-facing alias',
      )
    )
  }

  /**
   * Registration (Keycloak first login) blocked because the email already
   * belongs to an existing user (e.g. the Keycloak record was deleted and
   * recreated).
   */
  isExistingUserEmailRegistrationError(error: unknown): boolean {
    if (!this.isApiError(error)) {
      return false
    }

    return (
      error.response?.status === StatusCodes.UNAUTHORIZED &&
      error.response?.data?.['hydra:description']?.includes(
        'already exists on the platform',
      )
    )
  }

  /**
   * Any error where registration (Keycloak first login) was blocked and a
   * friendly explanation should be rendered instead of auto-logging out.
   */
  isRegistrationBlockedError(error: unknown): boolean {
    return (
      this.isPublicEmailAliasRegistrationError(error) ||
      this.isExistingUserEmailRegistrationError(error)
    )
  }

  isExpiredUserContextError(error: unknown): boolean {
    return (
      this.isInvalidUserContextError(error) &&
      (authService.hasCurrentUserType() ||
        this.areRequestAndResponseCommitHashesDifferent(error))
    )
  }

  isConstraintViolationListError(
    error: unknown,
  ): error is ConstraintViolationListError {
    return isConstraintViolationListError(error)
  }

  isInvalidUserContextError(error: unknown) {
    if (!this.isApiError(error)) {
      return false
    }

    return (
      error.response?.status === StatusCodes.BAD_REQUEST &&
      error.response.data['hydra:description'].includes(
        'Make sure the user context is set',
      ) // TODO: Would be nice if we could check an error code instead of the error description
    )
  }

  private areRequestAndResponseCommitHashesDifferent(error: unknown) {
    if (!this.isApiError(error)) {
      return false
    }

    const previousRequestCommitHash = localStorage.getItem(
      LOCAL_STORAGE_KEYS.apiCommitHash,
    )

    const responseCommitHash = error.response?.headers['x-commit-hash']

    return (
      !!responseCommitHash &&
      !!previousRequestCommitHash &&
      responseCommitHash !== previousRequestCommitHash
    )
  }

  isCloudflareChallengeResponse(error: unknown): boolean {
    if (!this.isAxiosError(error)) {
      return false
    }

    const headers = error.response?.headers || {}

    return headers['cf-mitigated'] === 'challenge'
  }

  isDuplicateClientErrorMessage(error: string): boolean {
    return error.includes('A client with this email address already exists')
  }

  isOutdatedAssetsError(error: unknown): boolean {
    if (!isError(error)) {
      return false
    }

    const errorMessage = error.message

    return (
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Importing a module script failed') ||
      errorMessage.includes('Unable to preload CSS')
    )
  }

  getEntityLevelViolationError(error: unknown): string | null {
    if (!this.isConstraintViolationListError(error)) {
      return null
    }

    const constraintViolations = error.response?.data?.violations || []

    // We assume that any violation that doesn't have `propertyPath` set is a
    // entity-level error (i.e., doesn't apply to any specific field).
    const entityError = constraintViolations.find(
      (violation: ConstraintViolation) => violation.propertyPath === '',
    )

    return entityError ? entityError.message : null
  }
}

const errorTypeService = new ErrorTypeService()

export default errorTypeService
